import { GeminiReportResponse } from './gemini'

interface CacheEntry {
    data: GeminiReportResponse
    timestamp: number
    cacheKey: string
}

const CACHE_PREFIX = 'ai_analysis_cache_'
const CACHE_DURATION = 24 * 60 * 60 * 1000 // 24 hours in milliseconds

/**
 * Generate a unique cache key based on analysis parameters
 */
export const generateCacheKey = (params: {
    type:
        | 'business_performance'
        | 'profitability'
        | 'financial_strength'
        | 'operating_capability'
        | 'management_efficiency'
        | 'balance_sheet'
    period_range: string
    selected_metrics: string[]
    data_hash: string // Hash of the data to detect changes
}): string => {
    const { type, period_range, selected_metrics, data_hash } = params

    // Sort metrics to ensure consistent key
    const sortedMetrics = [...selected_metrics].sort().join(',')

    return `${CACHE_PREFIX}${type}_${period_range}_${sortedMetrics}_${data_hash}`
}

/**
 * Create a simple hash from data
 */
export const createDataHash = (data: any[]): string => {
    // Create hash from data length + first/last item + selected fields
    if (!data || data.length === 0) return 'empty'

    const firstItem = data[0]
    const lastItem = data[data.length - 1]

    // Use key fields to create hash
    const hashString = JSON.stringify({
        length: data.length,
        first: firstItem.report_month,
        last: lastItem.report_month,
        // Add some values to detect data changes
        firstValue: Object.values(firstItem)[1], // first numeric value
        lastValue: Object.values(lastItem)[1],
    })

    // Simple hash function
    let hash = 0
    for (let i = 0; i < hashString.length; i++) {
        const char = hashString.charCodeAt(i)
        hash = (hash << 5) - hash + char
        hash = hash & hash // Convert to 32bit integer
    }

    return Math.abs(hash).toString(36)
}

/**
 * Get cached AI analysis result
 */
export const getCachedAnalysis = (
    cacheKey: string
): GeminiReportResponse | null => {
    try {
        const cached = localStorage.getItem(cacheKey)

        if (!cached) {
            console.log('📦 Cache miss:', cacheKey)
            return null
        }

        const entry: CacheEntry = JSON.parse(cached)
        const now = Date.now()

        // Check if cache is expired
        if (now - entry.timestamp > CACHE_DURATION) {
            console.log('⏰ Cache expired:', cacheKey)
            localStorage.removeItem(cacheKey)
            return null
        }

        console.log('✅ Cache hit:', cacheKey)
        return entry.data
    } catch (error) {
        console.error('Error reading cache:', error)
        return null
    }
}

/**
 * Save AI analysis result to cache
 */
export const setCachedAnalysis = (
    cacheKey: string,
    data: GeminiReportResponse
): void => {
    try {
        const entry: CacheEntry = {
            data,
            timestamp: Date.now(),
            cacheKey,
        }

        localStorage.setItem(cacheKey, JSON.stringify(entry))
        console.log('💾 Cache saved:', cacheKey)
    } catch (error) {
        console.error('Error saving cache:', error)
        // If localStorage is full, try to clear old caches
        clearExpiredCaches()
    }
}

/**
 * Clear all expired caches
 */
export const clearExpiredCaches = (): void => {
    try {
        const now = Date.now()
        const keys = Object.keys(localStorage)
        let clearedCount = 0

        keys.forEach((key) => {
            if (key.startsWith(CACHE_PREFIX)) {
                try {
                    const cached = localStorage.getItem(key)
                    if (cached) {
                        const entry: CacheEntry = JSON.parse(cached)

                        if (now - entry.timestamp > CACHE_DURATION) {
                            localStorage.removeItem(key)
                            clearedCount++
                        }
                    }
                } catch (_e) {
                    // Invalid cache entry, remove it
                    localStorage.removeItem(key)
                    clearedCount++
                }
            }
        })

        if (clearedCount > 0) {
            console.log(`🧹 Cleared ${clearedCount} expired cache entries`)
        }
    } catch (error) {
        console.error('Error clearing expired caches:', error)
    }
}

/**
 * Clear all AI analysis caches
 */
export const clearAllAIAnalysisCaches = (): void => {
    try {
        const keys = Object.keys(localStorage)
        let clearedCount = 0

        keys.forEach((key) => {
            if (key.startsWith(CACHE_PREFIX)) {
                localStorage.removeItem(key)
                clearedCount++
            }
        })

        console.log(`🧹 Cleared all ${clearedCount} AI analysis caches`)
    } catch (error) {
        console.error('Error clearing all caches:', error)
    }
}

/**
 * Get cache statistics
 */
export const getCacheStats = (): {
    totalCaches: number
    validCaches: number
    expiredCaches: number
    totalSize: number
} => {
    try {
        const keys = Object.keys(localStorage)
        const now = Date.now()
        let totalCaches = 0
        let validCaches = 0
        let expiredCaches = 0
        let totalSize = 0

        keys.forEach((key) => {
            if (key.startsWith(CACHE_PREFIX)) {
                totalCaches++
                const cached = localStorage.getItem(key)

                if (cached) {
                    totalSize += cached.length

                    try {
                        const entry: CacheEntry = JSON.parse(cached)

                        if (now - entry.timestamp > CACHE_DURATION) {
                            expiredCaches++
                        } else {
                            validCaches++
                        }
                    } catch (_e) {
                        expiredCaches++
                    }
                }
            }
        })

        return {
            totalCaches,
            validCaches,
            expiredCaches,
            totalSize,
        }
    } catch (error) {
        console.error('Error getting cache stats:', error)
        return {
            totalCaches: 0,
            validCaches: 0,
            expiredCaches: 0,
            totalSize: 0,
        }
    }
}
